import { useEffect, useState, useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import { PubkeySummary } from '../types/models/Pubkey';
import { ApiResponsePubkeys } from '../types/api/ApiResponsePubkey';

interface UsePubkeysReturn {
  pubkeys: PubkeySummary[];
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export const usePubkeys = (): UsePubkeysReturn => {
  const [pubkeys, setPubkeys] = useState<PubkeySummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const fetchPubkeys = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiFetch('/datacenters/pubkeys');

      // JSend: branch on body.status, never response.ok — an `error` arrives at HTTP 200 and must
      // surface as an error, not as an empty key list.
      const body = (await response.json().catch(() => null)) as ApiResponsePubkeys | null;
      if (body?.status === 'success' && Array.isArray(body.data?.pubkeys)) {
        setPubkeys(body.data.pubkeys);
      } else {
        setPubkeys([]);
        setError(new Error(body?.message ?? 'Could not load your public keys'));
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('An error occurred while fetching public keys'));
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    if (auth.isAuthenticated) {
      fetchPubkeys();
    }
  }, [auth.isAuthenticated, fetchPubkeys]);

  return { pubkeys, loading, error, refetch: fetchPubkeys };
};
